<template>
  <Form
    ref="observer"
    v-slot="{ meta }"
  >
    <b-message
      class="mt-4 info-pwd-message"
      has-icon
      size="is-medium"
      type="is-info"
    >
      <p :aria-label="$t('updateUser.info-pwd')">
        {{ $t("updateUser.info-pwd") }}
      </p>
    </b-message>
    <TextField
      v-model:value="password"
      type="password"
      label="login.pwd"
      name="change_password.password"
      place-holder="login.pwd-placeholder"
      :mandatory="true"
      horizontal
      @update:value="password = $event"
    />
    <text-field
      v-model:value="newPassword"
      type="password"
      place-holder="login.pwd-placeholder"
      :mandatory="true"
      label="login.lost-pwd.new-pwd"
      name="change_password.new_password"
      horizontal
      @update:value="newPassword = $event"
    />
    <text-field
      v-model:value="newPasswordConfirm"
      type="password"
      place-holder="login.pwd-placeholder"
      label="login.lost-pwd.new-pwd-confirm"
      name="change_password.confirm_new_password"
      rules="confirmed:change_password.new_password"
      :mandatory="true"
      horizontal
      @update:value="newPasswordConfirm = $event"
    />
    <div class="edit-pwd-footer">
      <!-- Bouton VRAIMENT desactive ( pas juste {@code :active} cosmetique )
           tant que le formulaire n'est pas valide . Avant ce fix , cliquer
           avec uniquement "Mot de passe actuel" rempli envoyait au backend
           un payload {@code newPassword: ""} -> {@code BAD_PASSWORDS} ->
           401 historique ( fix : 422 cote backend ) qui declenchait l'auto
           -logout Fetcher . Le {@code :disabled} bloque le submit en amont
           cote UX ; le 422 backend est defense-in-depth pour les futurs
           cas equivalents . -->
      <SaveActionButton
        :active="meta.valid"
        :disabled="!meta.valid"
        :aria-label="$t('login.aria-btn-login')"
        @click="validationPwd(password, newPassword, newPasswordConfirm)"
      />
    </div>
  </Form>
</template>

<script lang="ts">
import useText from "@/composable/components/text";
import services from "@/composable/services";
import TextField from "@/components/forms/TextField.vue";
import SaveActionButton from "@/components/common/buttons/SaveActionButton.vue";
import { Form } from "vee-validate";
import { BMessage } from "buefy";
import { ref } from "vue";
import i18n from "@/i18n";
import { CreateUserRequest } from "@/types/generated/UsedGeneratedTypes";
import { ErrorType } from "@/composable/application/errors";
import type { VeeForm } from "@/types/vee-validate";
import { useFieldErrorReset } from "@/composable/forms/useFieldErrorReset";
import { useCrossFieldConfirmation } from "@/composable/forms/useCrossFieldConfirmation";

export default {
  name: "EditPwd",
  components: { SaveActionButton, TextField, Form, BMessage },
  props: {
    login: {
      type: String,
      required: true,
    },
  },
  emits: ["changed-password"],
  setup(props, { emit }) {
    const password = useText().refText;
    const newPassword = useText().refText;
    const newPasswordConfirm = useText().refText;
    /**
     * Ref vers la balise {@code <Form ref="observer">} de vee-validate .
     * Permet d'appeler {@code setFieldError(field, message)} pour
     * marquer le champ "password" en rouge depuis l'erreur 422
     * BAD_CURRENT_PASSWORD ( ancien mot de passe KO ) , sans forcer
     * le logout - cf flow identique dans EditEmail .
     */
    const observer = ref<VeeForm | null>(null);

    // Reset auto de l'erreur server-side ( BAD_CURRENT_PASSWORD 422 )
    // sur le champ "password" des que l'utilisateur retape - evite
    // que le message d'erreur de la tentative precedente persiste
    // perturbant alors que le mdp est corrige .
    useFieldErrorReset(password, observer, "change_password.password");

    // Re-validation cross-field : la regle vee-validate
    // {@code confirmed:change_password.new_password} ne se redeclenche
    // PAS quand la source ( newPassword ) change , uniquement quand le
    // confirm change . Sans ca , modifier la source APRES avoir saisi
    // la confirmation laisse le confirm marque "valide" alors que les
    // valeurs ne matchent plus .
    useCrossFieldConfirmation(newPassword, observer, "change_password.confirm_new_password");

    async function validationPwd(password: string, newPassword: string, newPasswordConfirm: string) {
      try {
        const updateUserRequest = {} as CreateUserRequest;
        updateUserRequest.login = props.login;
        updateUserRequest.newPassword = newPassword;
        updateUserRequest.newPasswordConfirm = newPasswordConfirm;
        updateUserRequest.password = password;
        let response = await services.loginService.modifAcount(updateUserRequest);
        emit("changed-password", response);
        resetVariables();
      }
      catch (e: unknown) {
        const err = e as ErrorType;
        services.alertService.toastServerError(err);
        // 422 BAD_CURRENT_PASSWORD = mdp actuel KO . Marquer le champ
        // "change_password.password" en rouge en complement du toast .
        // Le backend retourne 422 ( pas 401 ) pour eviter l'auto-logout
        // cote Fetcher.ts - cf flow identique dans EditEmail .
        if (err?.httpResponseCode === 422) {
          observer.value?.setFieldError(
              "change_password.password",
              i18n.global.t("exceptionMessage.BAD_CURRENT_PASSWORD"),
          );
        }
      }
    }

    function resetVariables() {
      newPassword.value = "";
      newPasswordConfirm.value = "";
      password.value = "";
    }

    return {
      observer,
      password,
      newPasswordConfirm,
      newPassword,
      validationPwd,
      resetVariables,
    };
  },
};
</script>

<style scoped lang="scss">
/*
 * Footer du formulaire : positionne le bouton "save" a droite via
 * flex + justify-content:flex-end . La largeur intrinseque du bouton
 * est dictee par {@code <SaveActionButton>} ( 16.66% du conteneur ) ,
 * identique a EditEmail .
 */
.edit-pwd-footer {
  display: flex;
  justify-content: flex-end;
  margin-top: 0.5rem;
}
/*
 * Compactage du bandeau info ( meme convention que le bloc "Votre email
 * actuel" dans EditEmail ) : padding vertical reduit pour eviter le vide
 * vertical sur une ligne de texte unique .
 */
:deep(.info-pwd-message .message-body) {
  padding: 0.2em 1.5em;
}
</style>
